import { useState, useCallback, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * ProjectCarousel — fades between images, with arrow controls,
 * counter, and dot navigation. Keyboard left/right supported when hovered.
 */
export default function ProjectCarousel({ photos, title, testId }) {
  const [index, setIndex] = useState(0);
  const [hovered, setHovered] = useState(false);
  const count = photos.length;

  const next = useCallback(
    () => setIndex((i) => (i + 1) % count),
    [count]
  );
  const prev = useCallback(
    () => setIndex((i) => (i - 1 + count) % count),
    [count]
  );

  useEffect(() => {
    if (!hovered) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [hovered, next, prev]);

  return (
    <div
      data-testid={testId}
      className="group relative overflow-hidden bg-zinc-900"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="relative aspect-square w-full">
        <AnimatePresence initial={false} mode="wait">
          <motion.img
            key={index}
            src={photos[index]}
            alt={`${title} — ${index + 1}`}
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-0 w-full h-full object-cover"
            draggable={false}
          />
        </AnimatePresence>

        {count > 1 && (
          <>
            {/* Prev / Next */}
            <button
              type="button"
              onClick={prev}
              aria-label="Previous photo"
              data-testid={`${testId}-prev`}
              className="absolute left-4 sm:left-6 top-1/2 -translate-y-1/2 z-10 w-11 h-11 sm:w-12 sm:h-12 rounded-full border border-white/30 bg-zinc-950/40 backdrop-blur-md flex items-center justify-center text-white opacity-0 group-hover:opacity-100 hover:bg-zinc-950/70 hover:border-white/70 transition-all duration-500"
            >
              <ChevronLeft size={20} strokeWidth={1.5} />
            </button>
            <button
              type="button"
              onClick={next}
              aria-label="Next photo"
              data-testid={`${testId}-next`}
              className="absolute right-4 sm:right-6 top-1/2 -translate-y-1/2 z-10 w-11 h-11 sm:w-12 sm:h-12 rounded-full border border-white/30 bg-zinc-950/40 backdrop-blur-md flex items-center justify-center text-white opacity-0 group-hover:opacity-100 hover:bg-zinc-950/70 hover:border-white/70 transition-all duration-500"
            >
              <ChevronRight size={20} strokeWidth={1.5} />
            </button>

            {/* Counter */}
            <div className="absolute top-5 right-5 sm:top-6 sm:right-6 z-10 text-[11px] tracking-[0.25em] uppercase text-white/90 bg-zinc-950/40 backdrop-blur-md px-3 py-1.5 border border-white/20">
              {String(index + 1).padStart(2, "0")}
              <span className="mx-2 text-white/50">/</span>
              {String(count).padStart(2, "0")}
            </div>

            {/* Dots */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2.5">
              {photos.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  aria-label={`Go to photo ${i + 1}`}
                  data-testid={`${testId}-dot-${i}`}
                  onClick={() => setIndex(i)}
                  className={`h-1 transition-all duration-500 ${
                    i === index
                      ? "w-8 bg-zinc-100"
                      : "w-4 bg-zinc-100/40 hover:bg-zinc-100/70"
                  }`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
